(() => {
  const sectionIds = ['cart-drawer', 'cart-icon-bubble'];
  const protectionOptOutKey = 'dyefor.shipping-protection.opt-out';

  function requestConfig(body) {
    return {
      ...fetchConfig('javascript'),
      body: JSON.stringify(body),
    };
  }

  async function parseResponse(response) {
    const state = await response.json();

    if (!response.ok) {
      throw new Error(state.description || state.message || window.cartStrings?.error || 'Unable to update your cart.');
    }

    return state;
  }

  function sectionRequestData() {
    return {
      sections: sectionIds,
      sections_url: window.location.pathname,
    };
  }

  function renderCartSilently(state) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || !state.sections) return;

    drawer.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !state.sections[section.id]) return;

      sectionElement.innerHTML = drawer.getSectionInnerHTML(
        state.sections[section.id],
        section.selector
      );
    });

    const overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
    if (typeof drawer.syncEmptyState === 'function') drawer.syncEmptyState();
  }

  class DyeforShippingProtection extends HTMLElement {
    connectedCallback() {
      if (this.dataset.orphan === 'true') {
        this.removeOrphan();
        return;
      }

      this.toggle = this.querySelector('[data-protection-toggle]');
      this.status = this.querySelector('[data-protection-status]');
      if (!this.toggle || this.toggle.dataset.bound === 'true') return;

      this.toggle.dataset.bound = 'true';
      this.toggle.addEventListener('click', () => this.updateProtection());
      this.enableByDefault();
    }

    isOptedOut() {
      if (window.dyeforProtectionOptedOut === true) return true;

      try {
        return window.sessionStorage.getItem(protectionOptOutKey) === 'true';
      } catch (error) {
        return false;
      }
    }

    setOptedOut(optedOut) {
      window.dyeforProtectionOptedOut = optedOut;

      try {
        if (optedOut) {
          window.sessionStorage.setItem(protectionOptOutKey, 'true');
        } else {
          window.sessionStorage.removeItem(protectionOptOutKey);
        }
      } catch (error) {
        // The in-page preference still prevents an unwanted re-add.
      }
    }

    enableByDefault() {
      const enabled = this.toggle.getAttribute('aria-checked') === 'true';
      const variantId = this.dataset.variantId;

      if (enabled || !variantId || this.isOptedOut() || window.dyeforProtectionAutoAddRunning) return;

      window.dyeforProtectionAutoAddRunning = true;
      this.updateProtection({ automatic: true }).finally(() => {
        window.dyeforProtectionAutoAddRunning = false;
      });
    }

    async updateProtection({ automatic = false } = {}) {
      const enabled = this.toggle.getAttribute('aria-checked') === 'true';
      const variantId = this.dataset.variantId;
      const lineKey = this.dataset.lineKey;
      const wasOptedOut = this.isOptedOut();

      if ((!enabled && !variantId) || (enabled && !lineKey && !variantId)) return;

      if (!automatic) this.setOptedOut(enabled);
      this.setLoading(true);

      try {
        let response;

        if (enabled) {
          response = await fetch(
            routes.cart_change_url,
            requestConfig({
              id: lineKey || variantId,
              quantity: 0,
              ...sectionRequestData(),
            })
          );
        } else {
          response = await fetch(
            routes.cart_add_url,
            requestConfig({
              items: [
                {
                  id: Number(variantId),
                  quantity: 1,
                  properties: {
                    _dyefor_shipping_protection: 'true',
                  },
                },
              ],
              ...sectionRequestData(),
            })
          );
        }

        const state = await parseResponse(response);
        const drawer = document.querySelector('cart-drawer');

        if (drawer && state.sections) {
          drawer.renderContents(state);
        }

        document.dispatchEvent(new CustomEvent('dyefor:cart-updated', { detail: state }));
      } catch (error) {
        if (!automatic) this.setOptedOut(wasOptedOut);
        if (this.status) this.status.textContent = error.message;
        this.setLoading(false);
      }
    }

    async removeOrphan() {
      const lineKey = this.dataset.lineKey;
      if (!lineKey || window.dyeforProtectionCleanupRunning) return;

      window.dyeforProtectionCleanupRunning = true;

      try {
        const response = await fetch(
          routes.cart_change_url,
          requestConfig({
            id: lineKey,
            quantity: 0,
            ...sectionRequestData(),
          })
        );
        const state = await parseResponse(response);
        renderCartSilently(state);
      } catch (error) {
        console.error('Unable to remove orphaned shipping protection.', error);
      } finally {
        window.dyeforProtectionCleanupRunning = false;
      }
    }

    setLoading(loading) {
      this.classList.toggle('is-loading', loading);
      if (!this.toggle) return;

      this.toggle.disabled = loading;
      this.toggle.setAttribute('aria-busy', loading ? 'true' : 'false');
      if (this.status && loading) this.status.textContent = 'Updating cart...';
    }
  }

  if (!customElements.get('dyefor-shipping-protection')) {
    customElements.define('dyefor-shipping-protection', DyeforShippingProtection);
  }

  class DyeforCartPartyCompleter extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => event.stopPropagation());
      this.sourceProperties = this.parseJson('[data-party-source-properties]', {});
      this.companionVariants = this.parseJson('[data-party-companion-variants]', []);
      this.balloonProduct = this.parseJson('[data-party-balloon-product]', { variants: [] });
      this.companionCheckbox = this.querySelector('[data-party-companion]');
      this.companionSelect = this.querySelector('[data-party-companion-variant]');
      this.companionPrice = this.querySelector('[data-party-companion-price]');
      this.balloonCheckbox = this.querySelector('[data-party-balloon]');
      this.balloonColour = this.querySelector('[data-party-balloon-colour]');
      this.balloonNumber = this.querySelector('[data-party-balloon-number]');
      this.balloonPrice = this.querySelector('[data-party-balloon-price]');
      this.balloonSelectionText = this.querySelector('[data-party-balloon-selection]');
      this.summary = this.querySelector('[data-party-summary]');
      this.freeBadge = this.querySelector('[data-party-free-badge]');
      this.addButton = this.querySelector('[data-party-add]');
      this.status = this.querySelector('[data-party-status]');
      this.currentTotal = Number(this.dataset.currentTotal || 0);
      this.freeShippingThreshold = Number(this.dataset.freeShippingThreshold || 3000);
      this.defaultBalloonPrice = Number(this.dataset.balloonDefaultPrice || 1499);
      this.balloonProducts = new Map();
      this.selectedBalloonVariant = null;

      if (this.balloonProduct.handle) {
        this.balloonProducts.set(this.balloonProduct.handle, this.balloonProduct);
      }
      this.selectInitialBalloonVariant();

      this.companionCheckbox?.addEventListener('change', () => this.render());
      this.companionSelect?.addEventListener('change', () => this.render());
      this.balloonCheckbox?.addEventListener('change', () => this.onBalloonSelectionChange());
      this.balloonColour?.addEventListener('change', () => this.loadBalloonProduct(true));
      this.balloonNumber?.addEventListener('change', () => this.resolveBalloonVariant());
      this.addButton?.addEventListener('click', () => this.addSelectedPieces());

      if (this.balloonCheckbox?.checked && !this.selectedBalloonVariant) {
        this.loadBalloonProduct(false);
      } else {
        this.render();
      }
    }

    parseJson(selector, fallback) {
      const element = this.querySelector(selector);
      if (!element) return fallback;

      try {
        return JSON.parse(element.textContent);
      } catch (error) {
        console.error('Cart party-set data could not be read.', error);
        return fallback;
      }
    }

    getCompanionVariant() {
      if (!this.companionSelect) return null;
      const id = Number(this.companionSelect.value);
      return this.companionVariants.find((variant) => Number(variant.id) === id)
        || this.companionVariants.find((variant) => variant.available)
        || null;
    }

    selectInitialBalloonVariant() {
      const selectedId = Number(this.balloonProduct?.selectedVariantId);
      this.selectedBalloonVariant = this.balloonProduct?.variants?.find((variant) => (
        Number(variant.id) === selectedId && variant.available !== false
      )) || this.balloonProduct?.variants?.find((variant) => variant.available !== false) || null;

      if (this.selectedBalloonVariant) {
        this.updateBalloonPreview(this.selectedBalloonVariant, this.balloonProduct);
      }
    }

    async onBalloonSelectionChange() {
      if (this.balloonCheckbox?.checked && !this.selectedBalloonVariant) {
        if (this.balloonColour) {
          await this.loadBalloonProduct(false);
        } else {
          this.selectInitialBalloonVariant();
        }
      }
      this.render();
    }

    async loadBalloonProduct(forceReload) {
      if (!this.balloonColour) {
        this.selectInitialBalloonVariant();
        this.render();
        return;
      }
      const handle = this.balloonColour.value;
      this.setLoading(true, 'Checking balloon options...');

      try {
        let product = this.balloonProducts.get(handle);
        if (!product || forceReload) {
          const root = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${root}products/${handle}.js`, {
            headers: { Accept: 'application/json' },
          });
          product = await parseResponse(response);
          this.balloonProducts.set(handle, product);
        }

        this.balloonProduct = product;
        this.resolveBalloonVariant();
        this.clearStatus();
      } catch (error) {
        this.selectedBalloonVariant = null;
        this.showError(error.message || 'That balloon is not available right now.');
      } finally {
        this.setLoading(false);
        this.render();
      }
    }

    resolveBalloonVariant() {
      if (!this.balloonProduct) return;
      if (!this.balloonNumber) {
        this.selectInitialBalloonVariant();
        this.render();
        return;
      }
      const selectedNumber = String(this.balloonNumber.value);
      this.selectedBalloonVariant = this.balloonProduct.variants.find((variant) => {
        const option = variant.option1 ?? variant.options?.[0] ?? variant.title;
        return String(option) === selectedNumber && variant.available !== false;
      }) || null;
      this.updateBalloonPreview(this.selectedBalloonVariant, this.balloonProduct);
      this.render();
    }

    imageSource(variant, product) {
      return variant?.featured_image?.src
        || variant?.featured_image
        || product?.featured_image
        || product?.images?.[0]
        || '';
    }

    updateBalloonPreview(variant, product) {
      const source = this.imageSource(variant, product);
      if (source) {
        this.querySelectorAll('[data-party-balloon-preview] img').forEach((image) => {
          image.src = source;
          image.removeAttribute('srcset');
        });
      }

      if (this.balloonSelectionText && variant) {
        const option = variant.option1 ?? variant.options?.[0] ?? variant.title;
        this.balloonSelectionText.textContent = `Selected for this design: ${option}`;
      }
    }

    render() {
      const companionVariant = this.getCompanionVariant();
      const companionSelected = Boolean(this.companionCheckbox?.checked && companionVariant);
      const balloonSelected = Boolean(this.balloonCheckbox?.checked);
      const balloonPrice = Number(this.selectedBalloonVariant?.price || this.defaultBalloonPrice);
      let selectedTotal = this.currentTotal;

      if (companionSelected) selectedTotal += Number(companionVariant.price || 0);
      if (balloonSelected) selectedTotal += balloonPrice;

      if (this.companionPrice && companionVariant) {
        this.companionPrice.textContent = this.formatMoney(Number(companionVariant.price || 0));
      }
      if (this.balloonPrice) this.balloonPrice.textContent = this.formatMoney(balloonPrice);

      const remaining = Math.max(this.freeShippingThreshold - selectedTotal, 0);
      const hasSelection = companionSelected || balloonSelected;

      if (this.summary) {
        if (!hasSelection) {
          this.summary.textContent = 'Choose at least one matching piece.';
        } else if (remaining === 0) {
          this.summary.textContent = `New basket total ${this.formatMoney(selectedTotal)}`;
        } else {
          this.summary.textContent = `New basket total ${this.formatMoney(selectedTotal)} · ${this.formatMoney(remaining)} from free delivery`;
        }
      }

      if (this.freeBadge) this.freeBadge.hidden = !hasSelection || remaining > 0;
      if (this.addButton) {
        this.addButton.disabled = !hasSelection || (balloonSelected && !this.selectedBalloonVariant);
      }
    }

    sourcePropertiesForCompanion() {
      const properties = Array.isArray(this.sourceProperties)
        ? Object.fromEntries(this.sourceProperties)
        : { ...this.sourceProperties };

      Object.keys(properties).forEach((key) => {
        if (/^_Dyefor\b|^_Matches design$/i.test(key) || !properties[key]) {
          delete properties[key];
        }
      });

      return properties;
    }

    async addSelectedPieces() {
      const companionVariant = this.getCompanionVariant();
      const items = [];

      if (this.companionCheckbox?.checked && companionVariant) {
        items.push({
          id: Number(companionVariant.id),
          quantity: 1,
          properties: this.sourcePropertiesForCompanion(),
        });
      }

      if (this.balloonCheckbox?.checked) {
        if (!this.selectedBalloonVariant) await this.loadBalloonProduct(false);
        if (!this.selectedBalloonVariant) return;

        items.push({
          id: Number(this.selectedBalloonVariant.id),
          quantity: 1,
          properties: this.sourcePropertiesForCompanion(),
        });
      }

      if (!items.length) return;
      this.setLoading(true, 'Adding the matching pieces...');

      try {
        const response = await fetch(
          routes.cart_add_url,
          requestConfig({ items, ...sectionRequestData() })
        );
        const state = await parseResponse(response);
        const drawer = document.querySelector('cart-drawer');
        if (drawer && state.sections) drawer.renderContents(state);
        document.dispatchEvent(new CustomEvent('dyefor:cart-updated', { detail: state }));
      } catch (error) {
        this.showError(error.message || 'The matching pieces could not be added.');
        this.setLoading(false);
      }
    }

    formatMoney(cents) {
      const currency = window.Shopify?.currency?.active || 'GBP';
      const locale = document.documentElement.lang || 'en-GB';

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
        }).format(cents / 100);
      } catch (error) {
        return `GBP ${(cents / 100).toFixed(2)}`;
      }
    }

    setLoading(loading, message = '') {
      this.classList.toggle('is-loading', loading);
      if (this.addButton) this.addButton.setAttribute('aria-busy', loading ? 'true' : 'false');
      if (loading && this.status) this.status.textContent = message;
    }

    showError(message) {
      if (this.status) this.status.textContent = message;
    }

    clearStatus() {
      if (this.status) this.status.textContent = '';
    }
  }

  if (!customElements.get('dyefor-cart-party-completer')) {
    customElements.define('dyefor-cart-party-completer', DyeforCartPartyCompleter);
  }
})();
